import { useQuery } from '@apollo/client';
import { useNavigation, useRoute } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import { BaseText, Headline3, Meta, Spinner, theme } from 'aimo-ui';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import { READ_VERIFICATION_STATUS } from 'graphql/queries/readVerificationStatus';
import { usePermitsAndBenefits } from 'hooks/usePermitsAndBenefits';
import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useBenefitStore } from 'stores/benefitStore';
import {
  Query,
  QueryReadVerificationStatusArgs,
} from 'types/generatedSchemaTypes';
import { RootStackParamList } from 'types/navigation';
import { useUiStore } from 'stores/uiStore';

const POLL_INTERVAL_MS = 1000;
const MAX_ATTEMPTS = 100;

type RouteProps = StackScreenProps<RootStackParamList, 'benefitVerification'>;

const BenefitVerificationScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { bottom } = useSafeAreaInsets();
  const { params } = useRoute<RouteProps['route']>();
  const { verificationId, externalVerificationType, email } = params;
  const { setPrefillEmail } = useBenefitStore();
  const {
    customer: { uid: customerUid },
  } = useContext(AccountContext);
  const { benefits, refetchBenefits } = usePermitsAndBenefits({
    customerUid: customerUid || undefined,
  });
  const initialBenefitCountRef = useRef<number>(benefits?.length ?? 0);

  useEffect(() => {
    if (initialBenefitCountRef.current === 0 && benefits?.length) {
      initialBenefitCountRef.current = benefits.length;
    }
  }, [benefits]);

  const attemptsRef = useRef(0);
  const { startPolling, stopPolling } = useQuery<
    Pick<Query, 'readVerificationStatus'>,
    QueryReadVerificationStatusArgs
  >(READ_VERIFICATION_STATUS, {
    variables: {
      externalVerificationType,
      verificationId,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: async (data) => {
      const verificationStatus = data?.readVerificationStatus?.status;
      attemptsRef.current++;
      if (verificationStatus === 'SUCCESS') {
        stopPolling();
        try {
          const { data: refetchedData } = await refetchBenefits();
          const newCount = refetchedData?.readBenefits?.length ?? 0;
          if (newCount > initialBenefitCountRef.current) {
            showToast('account.benefitVerification.newBenefit', 'success');
          } else {
            showToast(
              t('account.benefitVerification.noBenefitFound', { email }),
              'success'
            );
          }
        } catch {
          showToast('account.benefitVerification.success', 'success');
        }
        navigation.goBack();
      } else if (verificationStatus === 'FAILED') {
        stopPolling();
        showToast('account.benefitVerification.failed', 'error');
        navigation.goBack();
      } else if (attemptsRef.current >= MAX_ATTEMPTS) {
        stopPolling();
        showToast('account.benefitVerification.timeout', 'error');
        navigation.goBack();
      }
    },
  });

  useEffect(() => {
    startPolling(POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const handleCancel = useCallback(() => {
    stopPolling();
    navigation.goBack();
  }, [stopPolling, navigation]);

  const handleResend = () => {
    setPrefillEmail(email);
    navigation.goBack();
    useUiStore.getState().setSheetModal('connectBenefit');
  };

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        <Spinner />
        <View style={styles.textContainer}>
          <Headline3 style={{ textAlign: 'center' }}>
            {t('account.benefitVerification.waitingForVerification')}
          </Headline3>
          <Meta
            color={theme.colors.gray200}
            style={{ textAlign: 'center', marginTop: 10 }}>
            {t('account.benefitVerification.waitingForVerificationDescription')}
          </Meta>
        </View>
      </View>
      <Pressable
        testID="resend-verification-button"
        onPress={handleResend}
        style={[styles.cancelButton, { paddingBottom: 10 }]}>
        <BaseText style={styles.cancelText}>
          {t('account.benefitVerification.resendEmail')}
        </BaseText>
      </Pressable>
      <Pressable
        testID="cancel-verification-button"
        onPress={handleCancel}
        style={[styles.cancelButton, { paddingBottom: bottom + 20 }]}>
        <BaseText style={[styles.cancelText]}>{t('button.cancel')}</BaseText>
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.white,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 40,
  },
  textContainer: {
    alignItems: 'center',
    marginTop: 30,
  },
  cancelButton: {
    alignItems: 'center',
    paddingTop: 20,
  },
  cancelText: {
    fontSize: 16,
    color: theme.colors.black,
    textDecorationLine: 'underline',
    fontWeight: '500',
  },
});

export default BenefitVerificationScreen;
